import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Modal, Form, Input, Select, InputNumber, Switch, Tag, message, Popconfirm, TreeSelect, Radio,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, ClearOutlined, CloudUploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as menuService from '../services/menuService';
import { staticMenuConfig, type StaticMenuItem } from '../constants/menu';

const MenuManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [menus, setMenus] = useState<menuService.Menu[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMenu, setEditingMenu] = useState<menuService.Menu | null>(null);
  const [form] = Form.useForm();

  const buildPermission = (menu: StaticMenuItem) => {
    const raw = menu.path || menu.key || menu.label;
    return raw.replace(/^\/+/, '').replace(/^menu-/, '').replace(/[/-]/g, '_');
  };

  const buildSyncMenus = (items: StaticMenuItem[]): menuService.SyncFrontendMenuRequest[] => items.map((item, index) => ({
    key: item.key,
    name: item.label,
    path: item.path,
    icon: item.icon,
    permission: buildPermission(item),
    sort: index + 1,
    children: item.children ? buildSyncMenus(item.children) : undefined,
  }));

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const treeRes = await menuService.getMenuTree();
      setMenus(treeRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = (parentId?: string) => {
    setEditingMenu(null);
    form.resetFields();
    form.setFieldsValue({
      parentId: parentId || null,
      type: 2,
      sort: 0,
      visible: 1,
      status: 1,
      isExternal: 0,
      isCache: 0,
    });
    setModalVisible(true);
  };

  const handleEdit = (record: menuService.Menu) => {
    setEditingMenu(record);
    form.setFieldsValue({
      ...record,
      parentId: record.parentId || null,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await menuService.deleteMenu(id);
      message.success('删除成功');
      loadData();
    } catch {
      message.error('删除失败');
    }
  };

  const handleClearCache = async () => {
    try {
      await menuService.clearMenuCache();
      message.success('缓存已清除');
      loadData();
    } catch {
      message.error('清除缓存失败');
    }
  };

  const handleSyncFrontendMenus = async () => {
    try {
      setLoading(true);
      await menuService.syncFrontendMenus(buildSyncMenus(staticMenuConfig));
      message.success('前端菜单已同步到后端');
      await loadData();
    } catch {
      message.error('同步前端菜单失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingMenu) {
        await menuService.updateMenu(editingMenu.id, values);
        message.success('更新成功');
      } else {
        await menuService.createMenu(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadData();
    } catch {
      message.error('操作失败');
    }
  };

  // 构建TreeSelect数据
  const buildTreeSelectData = (menus: menuService.Menu[], excludeId?: string): any[] => {
    return menus
      .filter(m => m.id !== excludeId && m.type !== 3) // 排除当前编辑的菜单和按钮类型
      .map(m => ({
        value: m.id,
        title: m.name,
        children: m.children ? buildTreeSelectData(m.children, excludeId) : [],
      }));
  };

  const columns: ColumnsType<menuService.Menu> = [
    {
      title: '菜单名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      render: (icon: string) => icon || '-',
    },
    {
      title: '路由路径',
      dataIndex: 'path',
      key: 'path',
      width: 200,
      render: (path: string) => path || '-',
    },
    {
      title: '权限标识',
      dataIndex: 'permission',
      key: 'permission',
      width: 150,
      render: (permission: string) => permission ? <Tag color="blue">{permission}</Tag> : '-',
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: number) => {
        const info = menuService.menuTypeMap[type];
        return <Tag color={info?.color}>{info?.text}</Tag>;
      },
    },
    {
      title: '排序',
      dataIndex: 'sort',
      key: 'sort',
      width: 80,
    },
    {
      title: '可见',
      dataIndex: 'visible',
      key: 'visible',
      width: 80,
      render: (visible: number) => (
        <Tag color={visible === 1 ? 'green' : 'red'}>{visible === 1 ? '显示' : '隐藏'}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '启用' : '禁用'}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          {record.type !== 3 && (
            <Button size="small" type="link" onClick={() => handleAdd(record.id)}>
              添加子菜单
            </Button>
          )}
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定要删除此菜单吗？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="菜单管理"
        extra={
          <Space>
            <Button icon={<CloudUploadOutlined />} onClick={handleSyncFrontendMenus}>同步前端菜单</Button>
            <Button icon={<ClearOutlined />} onClick={handleClearCache}>清除缓存</Button>
            <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAdd()}>
              新增菜单
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={menus}
          rowKey="id"
          loading={loading}
          pagination={false}
          expandable={{ defaultExpandAllRows: true }}
        />
      </Card>

      <Modal
        title={editingMenu ? '编辑菜单' : '新增菜单'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        width={700}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="parentId" label="上级菜单">
            <TreeSelect
              allowClear
              placeholder="选择上级菜单（不选则为顶级菜单）"
              treeData={buildTreeSelectData(menus, editingMenu?.id)}
              treeDefaultExpandAll
            />
          </Form.Item>

          <Form.Item name="type" label="菜单类型" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value={1}>目录</Radio>
              <Radio value={2}>菜单</Radio>
              <Radio value={3}>按钮</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="name" label="菜单名称" rules={[{ required: true, message: '请输入菜单名称' }]}>
            <Input placeholder="请输入菜单名称" />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
            {({ getFieldValue }) => {
              const type = getFieldValue('type');
              return type !== 3 ? (
                <>
                  <Form.Item name="path" label="路由路径">
                    <Input placeholder="请输入路由路径，如 /user-management" />
                  </Form.Item>
                  <Form.Item name="icon" label="菜单图标">
                    <Select allowClear placeholder="选择图标">
                      {menuService.iconList.map(icon => (
                        <Select.Option key={icon} value={icon}>{icon}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              ) : null;
            }}
          </Form.Item>

          <Form.Item name="permission" label="权限标识">
            <Input placeholder="请输入权限标识，如 user_management" />
          </Form.Item>

          <Form.Item name="sort" label="排序号">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小越靠前" />
          </Form.Item>

          <Space size="large">
            <Form.Item name="visible" label="是否可见" valuePropName="checked" getValueFromEvent={(checked) => checked ? 1 : 0} getValueProps={(value) => ({ checked: value === 1 })}>
              <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
            </Form.Item>
            <Form.Item name="status" label="状态" valuePropName="checked" getValueFromEvent={(checked) => checked ? 1 : 0} getValueProps={(value) => ({ checked: value === 1 })}>
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </Space>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuManagement;
